import { UserManager, WebStorageStateStore } from "oidc-client";
import {
  ApplicationName,
  AuthorizationPaths,
  QueryParameterNames,
} from "./ApiAuthorizationConstants";

let _callbacks = [];
let _nextSubscriptionId = 0;
let _user = null;
let _isAuthenticated = false;

let _popUpDisabled = true;

var hostAndPort = window.location.origin;

let settings = {
  authority: process.env.REACT_APP_STS_AUTHORITY,
  client_id: process.env.REACT_APP_STS_CLIENT_ID,
  response_type: process.env.REACT_APP_STS_RESPONSE_TYPE,
  scope: process.env.REACT_APP_STS_SCOPE,
  redirect_uri: `${hostAndPort}${AuthorizationPaths.LoginCallback}`,
  post_logout_redirect_uri: `${hostAndPort}${AuthorizationPaths.LogOutCallback}`,
};

settings.automaticSilentRenew = true;
settings.includeIdTokenInSilentRenew = true;
settings.userStore = new WebStorageStateStore({
  prefix: ApplicationName,
});

const userManager = new UserManager(settings);

//console.log(userManager);

userManager.events.addUserSignedOut(async () => {
  await userManager.removeUser();
  updateState(undefined);
});

const isAuthenticated = async () => {
  const user = await getUser();
  return !!user;
};

const getUser = async () => {
  if (_user && _user.profile) {
    return _user.profile;
  }

  const user = await userManager.getUser();
  return user && user.profile;
};

const getAccessToken = async () => {
  const user = await userManager.getUser();
  return user && user.access_token;
};

const signIn = async (state) => {
  try {
    const silentUser = await userManager.signinSilent(createArguments());
    updateState(silentUser);
    return success(state);
  } catch (silentError) {
    // User might not be authenticated, fallback to popup authentication
    console.log("Silent authentication error: ", silentError);

    try {
      if (_popUpDisabled) {
        throw new Error(
          "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
        );
      }

      const popUpUser = await userManager.signinPopup(createArguments());
      updateState(popUpUser);
      return success(state);
    } catch (popUpError) {
      if (popUpError.message === "Popup window closed") {
        return error("The user closed the window.");
      } else if (!_popUpDisabled) {
        console.log("Popup authentication error: ", popUpError);
      }

      try {
        await userManager.signinRedirect(createArguments(state));
        return redirect();
      } catch (redirectError) {
        console.log("Redirect authentication error: ", redirectError);
        return error(redirectError);
      }
    }
  }
};

const completeSignIn = async (url) => {
  try {
    const user = await userManager.signinCallback(url);
    updateState(user);
    return success(user && user.state);
  } catch (error) {
    console.log("There was an error signing in: ", error);
    return error("There was an error signing in.");
  }
};

// We try to sign out the user in two different ways:
// 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
//    Pop-Up blocker or the user has disabled PopUps.
// 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
//    post logout redirect flow.
const signOut = async (returnUrl) => {
  const state = { returnUrl };
  try {
    if (_popUpDisabled) {
      throw new Error(
        "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it."
      );
    }

    await userManager.signoutPopup(this.createArguments());
    updateState(undefined);
    return success(state);
  } catch (popupSignOutError) {
    console.log("Popup signout error: ", popupSignOutError);
    try {
      await userManager.signoutRedirect(createArguments(state));
      return redirect();
    } catch (redirectSignOutError) {
      console.log("Redirect signout error: ", redirectSignOutError);
      return error(redirectSignOutError);
    }
  }
};

const completeSignOut = async (url) => {
  try {
    const response = await userManager.signoutCallback(url);
    updateState(null);
    return success(response && response.data);
  } catch (error) {
    console.log(`There was an error trying to log out '${error}'.`);
    return error(error);
  }
};

const updateState = (user) => {
  _user = user;
  _isAuthenticated = !!_user;
  notifySubscribers();
};

const subscribe = (callback) => {
  _callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
  return _nextSubscriptionId - 1;
};

const unsubscribe = (subscriptionId) => {
  const subscriptionIndex = _callbacks
    .map((element, index) =>
      element.subscription === subscriptionId ? { found: true, index } : { found: false }
    )
    .filter((element) => element.found === true);
  if (subscriptionIndex.length !== 1) {
    throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
  }

  _callbacks.splice(subscriptionIndex[0].index, 1);
};

const notifySubscribers = () => {
  for (let i = 0; i < _callbacks.length; i++) {
    const callback = _callbacks[i].callback;
    callback();
  }
};

const createArguments = (state) => {
  return { useReplaceToNavigate: true, data: state };
};

const error = (message) => {
  return { status: AuthenticationResultStatus.Fail, message };
};

const success = (state) => {
  return { status: AuthenticationResultStatus.Success, state };
};

const redirect = () => {
  return { status: AuthenticationResultStatus.Redirect };
};

const getReturnUrl = (state) => {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get(QueryParameterNames.ReturnUrl);
  if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
    // This is an extra check to prevent open redirects.
    throw new Error(
      "Invalid return url. The return url needs to have the same origin as the current page."
    );
  }
  return (state && state.returnUrl) || fromQuery || `${window.location.origin}/`;
};

const login = async (returnUrl) => {
  const state = { returnUrl };
  const result = await signIn(state);
  switch (result.status) {
    case AuthenticationResultStatus.Redirect:
      break;
    case AuthenticationResultStatus.Success:
      navigateToReturnUrl(returnUrl);
      break;
    case AuthenticationResultStatus.Fail:
      break;
    default:
      console.log(`Invalid status result: '${result.status}'`);
  }
};

const processLoginCallback = async () => {
  const url = window.location.href;
  const result = await completeSignIn(url);
  switch (result.status) {
    case AuthenticationResultStatus.Redirect:
      // There should not be any redirects as the only time completeSignIn finishes
      // is when we are doing a redirect sign in flow.
      throw new Error("Should not redirect.");
    case AuthenticationResultStatus.Success:
      navigateToReturnUrl(getReturnUrl(result.state));
      break;
    case AuthenticationResultStatus.Fail:
      break;
    default:
      throw new Error(`Invalid authentication result status '${result.status}'.`);
  }
};

const processLogoutCallback = async () => {
  const url = window.location.href;
  const result = await completeSignOut(url);
  switch (result.status) {
    case AuthenticationResultStatus.Redirect:
      // There should not be any redirects as the only time completeAuthentication finishes
      // is when we are doing a redirect sign in flow.
      throw new Error("Should not redirect.");
    case AuthenticationResultStatus.Success:
      await navigateToReturnUrl(getReturnUrl(result.state));
      break;
    default:
      throw new Error("Invalid authentication result status.");
  }
};

const navigateToReturnUrl = (returnUrl) => {
  // It's important that we do a replace here so that we remove the callback uri with the
  // fragment containing the tokens from the browser history.
  window.location.replace(returnUrl);
};

export {
  isAuthenticated,
  getReturnUrl,
  signIn,
  completeSignIn,
  completeSignOut,
  signOut,
  login,
  processLoginCallback,
  processLogoutCallback,
  navigateToReturnUrl,
  getAccessToken,
};

export const AuthenticationResultStatus = {
  Redirect: "redirect",
  Success: "success",
  Fail: "fail",
};
